import React, { FC, useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Divider } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Training } from '../../../models/training.models';
import { formatDuration, getMeasurementUnit } from '../../../helpers/general.helpers';
import { WORKOUT_LOG_ZONE_DISPLAY_CONFIG } from '../components/workout-zone.constants';
import useService from '../../../hooks/service.hook';
import TrainingsService from '../../../services/trainings.service';
import { useSnackbar } from 'notistack';
import COETrainingDurationInput from '../../coe-training-duration-input/coe-training-duration-input.component';
import { palette } from '../../../theme';

export interface LogTabProps {
  training: Training;
  trainingType: 'athlete' | 'coach' | 'guardian';
  onRefresh?: () => void;
  onEdit?: () => void;
  readOnly?: boolean;
}

const LogTab: FC<LogTabProps> = ({ training, onRefresh, readOnly = false }) => {
  const trainingsService = useService(TrainingsService);
  const { enqueueSnackbar } = useSnackbar();

  const durationInit = training?.workoutData?.durationComplete || [0, 0, 0, 0, 0, 0];
  const distanceInit = training?.workoutData?.distanceComplete || [0, 0, 0, 0, 0, 0];

  const [duration, setDuration] = useState([...durationInit]);
  const [distance, setDistance] = useState([...distanceInit]);

  useEffect(() => {
    setDuration([...durationInit]);
    setDistance([...distanceInit]);
  }, [training]);

  const handleDurationChange = (seconds: number, idx: number) => {
    const arr = [...duration];
    arr[idx] = seconds;
    setDuration(arr);
  };

  const handleDistanceChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const arr = [...distance];
    arr[idx] = parseFloat(e.target.value) || 0;
    setDistance(arr);
  };

  const handleSave = () => {
    // Zone 0 is not editable on this tab — always clear it on save
    const durationComplete = [0, ...duration.slice(1)];
    const distanceComplete = [0, ...distance.slice(1)];

    trainingsService
      .logWorkoutData(training.id.toString(), { distanceComplete, durationComplete })
      .then(() => {
        enqueueSnackbar('Workout data saved successfully', { variant: 'success' });
        if (onRefresh) {
          onRefresh();
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to save workout data', { variant: 'error' });
      });
  };

  const totalDuration = duration.slice(1).reduce((acc, val) => acc + (val || 0), 0);
  const totalDistance = distance.slice(1).reduce((acc, val) => acc + (val || 0), 0);

  const measurementUnit = getMeasurementUnit();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 2, pt: 3, pb: 1 }}>
        {/* Section header */}
        <Typography
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            fontSize: 11,
            fontWeight: 600,
            color: palette.text.secondary,
          }}
        >
          Log Training Data
        </Typography>

        {/* Column headers — same grid as zone rows */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '16px 52px 1fr 100px 60px',
            columnGap: 1,
            alignItems: 'center',
            px: 1,
            mb: 1,
          }}
        >
          <Box />
          <Box />
          <Box />
          <Typography
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: 10,
              fontWeight: 600,
              color: palette.text.secondary,
              textAlign: 'center',
            }}
          >
            Hours
          </Typography>
          <Typography
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: 10,
              fontWeight: 600,
              color: palette.text.secondary,
              textAlign: 'center',
            }}
          >
            {measurementUnit === 'km' ? 'KM' : 'Miles'}
          </Typography>
        </Box>

        {/* Total row — same grid as zone rows */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '16px 52px 1fr 100px 60px',
            columnGap: 1,
            alignItems: 'center',
            py: 1.5,
            px: 1,
            backgroundColor: palette.background.subtle,
            borderRadius: 2,
            border: `1px solid ${palette.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            mb: 1,
          }}
        >
          <FiberManualRecordIcon sx={{ fontSize: 12, color: palette.text.primary }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: palette.text.primary }}>
            Total
          </Typography>
          <Box />
          <Typography variant="subtitle2" fontWeight={700} sx={{ textAlign: 'center', color: palette.text.primary }}>
            {formatDuration(totalDuration)} hr
          </Typography>
          <Typography variant="subtitle2" fontWeight={700} sx={{ textAlign: 'center', color: palette.text.primary }}>
            {totalDistance.toFixed(1)} {measurementUnit}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Zone rows — grid ensures bar takes remaining space, inputs never overflow */}
        {WORKOUT_LOG_ZONE_DISPLAY_CONFIG.map(({ label, color, zoneIdx }) => {
          const barFill = totalDuration > 0 ? (duration[zoneIdx] / totalDuration) * 100 : 0;

          return (
            <Box
              key={label}
              sx={{
                display: 'grid',
                gridTemplateColumns: '16px 52px 1fr 100px 60px',
                columnGap: 1,
                alignItems: 'center',
                py: 1.25,
                px: 1,
                borderRadius: 1.5,
                transition: 'background-color 0.12s ease',
                '&:hover': { backgroundColor: 'rgba(13, 97, 255, 0.04)' },
              }}
            >
              {/* Col 1: dot */}
              <FiberManualRecordIcon sx={{ fontSize: 12, color }} />

              {/* Col 2: label */}
              <Typography variant="subtitle2" sx={{ color: palette.text.primary }}>
                {label}
              </Typography>

              {/* Col 3: proportional bar — takes all remaining width */}
              <Box sx={{ overflow: 'hidden', borderRadius: '3px', height: 6, backgroundColor: palette.grey[200] }}>
                <Box
                  sx={{
                    width: `${barFill}%`,
                    height: '100%',
                    backgroundColor: color,
                    borderRadius: '3px',
                    transition: 'width 0.2s ease',
                  }}
                />
              </Box>

              {/* Col 4: duration input */}
              <Box
                sx={{
                  '& .MuiTextField-root': { width: '100%' },
                  ...(readOnly && { pointerEvents: 'none', opacity: 0.7 }),
                }}
              >
                <COETrainingDurationInput
                  value={duration[zoneIdx]}
                  onChange={(seconds) => handleDurationChange(seconds, zoneIdx)}
                />
              </Box>

              {/* Col 5: distance input */}
              <TextField
                value={distance[zoneIdx] || '0.0'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDistanceChange(e, zoneIdx)}
                size="small"
                type="number"
                label="Distance"
                disabled={readOnly}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& input': { textAlign: 'center', py: '8px', px: '2px' },
                }}
                inputProps={{ min: 0, step: 0.1 }}
                placeholder="0.0"
              />
            </Box>
          );
        })}
      </Box>

      {/* Sticky bottom action bar */}
      {!readOnly && (
        <Box
          sx={{
            backgroundColor: palette.background.paper,
            px: 3,
            pb: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 3 }}
          >
            Save Changes
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LogTab;
